import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ShoppingBag, User, Bell, ChevronDown, LogOut, Menu, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AuthModal from '@/components/AuthModal';

import { API_STRING as api } from '@/App';

import { logger } from '@/utils/logger';
export default function Navigation({ user, setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const openAuth = () => setShowAuth(true);
    window.addEventListener('mementa:open-auth', openAuth);
    return () => window.removeEventListener('mementa:open-auth', openAuth);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll for new notifications every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${api}/notifications/unread-count`, {
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      logger.error('Error fetching unread count:', error);
    }
  };

  const navLinks = [
    { path: '/', label: 'Browse', icon: ShoppingBag },
    { path: '/about', label: 'About' },
    ...(user ? [{ path: '/keepsakes', label: 'Keepsakes' }] : []),
    ...(user?.is_admin ? [{ path: '/admin/dashboard', label: 'Admin Dashboard', icon: User, isAdmin: true }] : [])
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 backdrop-blur-md" style={{background: 'rgba(254, 215, 165, 0.95)', borderBottom: '1px solid rgba(32, 33, 43, 0.1)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
              <span className="text-2xl sm:text-3xl" style={{fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: '600'}}>Mementa</span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navLinks.map(link => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    className="flex items-center space-x-2 text-base font-medium transition-colors"
                    style={{
                      color: location.pathname === link.path ? '#9E6752' : (link.isAdmin ? '#9E6752' : '#534145'),
                      fontWeight: link.isAdmin ? '600' : 'normal'
                    }}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span>{link.label}</span>
                  </Link>
                );
              })}

              {/* Desktop Auth Section */}
              {user ? (
                <div className="flex items-center space-x-3">
                  {/* `modal={false}` prevents Radix from locking body
                      scroll (data-scroll-locked + position:relative +
                      overflow:hidden on <body>), which would otherwise
                      kill `position: sticky` on the nav itself and on
                      the Browse filter bar, making them snap back to
                      their natural document positions while the dropdown
                      is open. This menu is a tiny popover, no scroll
                      lock needed. */}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="profile-menu-trigger"
                        className="relative flex items-center space-x-2 text-sm font-medium hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9E6752] rounded-md px-1"
                        style={{ color: (location.pathname === '/profile' || location.pathname === '/notifications') ? '#9E6752' : '#534145' }}
                      >
                        <User className="w-4 h-4" />
                        <span>{user.name}</span>
                        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                        {unreadCount > 0 && (
                          <span
                            data-testid="profile-unread-badge"
                            className="absolute -top-1 -right-2 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white rounded-full"
                            style={{ background: '#DC2626' }}
                          >
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        data-testid="profile-menu-account"
                        onSelect={() => navigate('/profile')}
                        className="cursor-pointer"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Account
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="profile-menu-notifications"
                        onSelect={() => navigate('/notifications')}
                        className="cursor-pointer flex items-center justify-between"
                      >
                        <span className="flex items-center">
                          <Bell className="w-4 h-4 mr-2" />
                          Notifications
                        </span>
                        {unreadCount > 0 && (
                          <span
                            data-testid="profile-menu-unread-badge"
                            className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white rounded-full"
                            style={{ background: '#DC2626' }}
                          >
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid="profile-menu-logout"
                        onSelect={handleLogout}
                        className="cursor-pointer"
                        style={{ color: '#9E6752' }}
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuth(true)}
                  data-testid="login-button"
                  style={{background: '#9E6752', color: 'white', borderRadius: '20px', padding: '10px 24px'}}
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{color: '#20212B'}}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t" style={{borderColor: 'rgba(32, 33, 43, 0.1)'}}>
              <div className="flex flex-col space-y-4">
                {navLinks.map(link => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                      className="flex items-center space-x-3 px-2 py-2 rounded-lg text-base font-medium transition-colors"
                      style={{
                        color: location.pathname === link.path ? '#9E6752' : '#534145',
                        background: location.pathname === link.path ? 'rgba(158, 103, 82, 0.1)' : 'transparent'
                      }}
                    >
                      {Icon && <Icon className="w-5 h-5" />}
                      <span>{link.label}</span>
                    </Link>
                  );
                })}

                {/* Mobile Auth Section */}
                <div className="pt-4 border-t" style={{borderColor: 'rgba(32, 33, 43, 0.1)'}}>
                  {user ? (
                    <>
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-3 px-2 py-2 mb-2 rounded-lg transition-colors"
                        style={{
                          color: location.pathname === '/profile' ? '#9E6752' : '#534145',
                          background: location.pathname === '/profile' ? 'rgba(158, 103, 82, 0.1)' : 'transparent'
                        }}
                      >
                        <User className="w-5 h-5" />
                        <span className="text-sm font-medium">{user.name} (Account)</span>
                      </Link>
                      <Link
                        to="/notifications"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid="mobile-nav-notifications"
                        className="flex items-center justify-between px-2 py-2 mb-2 rounded-lg transition-colors"
                        style={{
                          color: location.pathname === '/notifications' ? '#9E6752' : '#534145',
                          background: location.pathname === '/notifications' ? 'rgba(158, 103, 82, 0.1)' : 'transparent'
                        }}
                      >
                        <span className="flex items-center space-x-3">
                          <Bell className="w-5 h-5" />
                          <span className="text-sm font-medium">Notifications</span>
                        </span>
                        {unreadCount > 0 && (
                          <span
                            className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white rounded-full"
                            style={{ background: '#DC2626' }}
                          >
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </Link>
                      <Button
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="w-full flex items-center justify-center space-x-2"
                        style={{background: '#9E6752', color: 'white', borderRadius: '8px'}}
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => {
                        setShowAuth(true);
                        setMobileMenuOpen(false);
                      }}
                      data-testid="login-button"
                      className="w-full"
                      style={{background: '#9E6752', color: 'white', borderRadius: '8px'}}
                    >
                      Sign In
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} setUser={setUser} />}
    </>
  );
}